import api from '../../config/axios';

// ============================================================================
// STATIC DATA CACHE
// Cache for frequently accessed static data to improve performance
// ============================================================================
let courseFiltersCache = null;
let stagesCache = null;
let categoriesCache = null;
let subjectsCache = null;

// ============================================================================
// STUDENT SERVICE API
// All endpoints for student-related operations
// ============================================================================

export const studentService = {
  // ============================================================================
  // DASHBOARD API
  // ============================================================================
  
  /**
   * Get student dashboard data (includes recent activity)
   * GET /api/student/dashboard
   * Purpose: Main dashboard information including recent activities
   * @returns {Promise} Response with dashboard data including recent activities
   */
  getDashboard: async () => {
    try {
      const response = await api.get('/student/dashboard');
      return response.data;
    } catch (error) {
      // Return empty dashboard data if endpoint fails
      return {
        student: {},
        subscriptions: [],
        recentLessons: [],
        unansweredQuestions: [],
        stats: {
          activeSubscriptions: 0,
          recentLessons: 0,
          unansweredQuestions: 0
        }
      };
    }
  },

  /**
   * Get recent activity from dashboard data
   * Purpose: Extract recent activity from dashboard endpoint
   * @returns {Promise} Response with recent activities
   */
  getRecentActivity: async () => {
    try {
      // Use the same dashboard data instead of making duplicate request
      const data = await studentService.getDashboard();
      
      const activities = [];
      
      // Add recent lessons
      if (data.recentLessons && Array.isArray(data.recentLessons)) {
        data.recentLessons.forEach(lesson => {
          activities.push({
            id: `lesson-${lesson.id}`,
            type: 'lesson',
            description: `شاهدت درس: ${lesson.title}`,
            time: new Date(lesson.completed_at || lesson.created_at).toLocaleDateString('ar-SA'),
            course_title: lesson.course_title,
            teacher_name: lesson.teacher_name
          });
        });
      }
      
      // Add unanswered questions
      if (data.unansweredQuestions && Array.isArray(data.unansweredQuestions)) {
        data.unansweredQuestions.forEach(question => {
          activities.push({
            id: `question-${question.id}`,
            type: 'question',
            description: `سألت عن: ${question.text.substring(0, 50)}${question.text.length > 50 ? '...' : ''}`,
            time: new Date(question.created_at).toLocaleDateString('ar-SA'),
            lesson_title: question.lesson_title,
            course_title: question.course_title,
            teacher_name: question.teacher_name
          });
        });
      }
      
      // Sort by date (most recent first)
      activities.sort((a, b) => new Date(b.time) - new Date(a.time));
      
      return { activities };
    } catch (error) {
      return { activities: [] };
    }
  },

  // ============================================================================
  // SUBSCRIPTIONS API
  // ============================================================================
  
  /**
   * Get student subscriptions
   * GET /api/student/subscriptions
   * Purpose: List all active subscriptions for student
   * @returns {Promise} Response with subscriptions array
   */
  getSubscriptions: async () => {
      const response = await api.get('/student/subscriptions');
      return response.data;
  },

  // ============================================================================
  // ACTIVITY API
  // ============================================================================
  
  /**
   * Get student subscription info from subscriptions data
   * Purpose: Get student's subscription information from subscriptions endpoint
   * @returns {Promise} Response with subscription info
   */
  getSubscriptionInfo: async () => {
    const subscriptionsData = await api.get('/student/subscriptions');
    const activeSubscriptions = subscriptionsData.data.subscriptions?.filter(sub => sub.status === 'active') || [];
    return {
      activeSubscriptions: activeSubscriptions.length,
      totalSubscriptions: subscriptionsData.data.subscriptions?.length || 0,
      subscriptions: activeSubscriptions
    };
  },

  /**
   * Get recent lessons from dashboard data
   * Purpose: Get student's recent lessons from dashboard endpoint
   * @returns {Promise} Response with recent lessons
   */
  getRecentLessons: async () => {
    try {
      // Use the same dashboard data instead of making duplicate request
      const data = await studentService.getDashboard();
      return {
        lessons: data.recentLessons || []
      };
    } catch (error) {
      // Return empty data if dashboard endpoint fails
      return { lessons: [] };
    }
  },

  /**
   * Get student lessons with filtering
   * GET /api/student/lessons
   * Purpose: Get all lessons for student across all subscribed courses with optional filtering
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise} Response with lessons data
   */
  getStudentLessons: async (params = {}) => {
    try {
      // First get student's active subscriptions to get course IDs
      const subscriptionsData = await api.get('/student/subscriptions');
      const activeSubscriptions = subscriptionsData.data.subscriptions?.filter(sub => sub.status === 'active') || [];
      
      if (activeSubscriptions.length === 0) {
        return [];
      }

      // Extract course IDs from subscriptions
      const courseIds = activeSubscriptions.map(sub => sub.course_id).filter(Boolean);
      
      if (courseIds.length === 0) {
        return [];
      }

      // Fetch lessons for all courses in single request with courseIds parameter
      const queryParams = new URLSearchParams({
        courseIds: courseIds.join(',')
      });

      // Add additional filter parameters
      Object.keys(params).forEach(key => {
        if (params[key]) queryParams.append(key, params[key]);
      });

      const response = await api.get(`/student/lessons?${queryParams}`);
      
      // API returns array directly, add progress data for each lesson
      const lessons = Array.isArray(response.data) ? response.data : (response.data.lessons || response.data || []);
      
      return lessons.map(lesson => ({
        ...lesson,
        progress: lesson.completed ? 100 : (lesson.progress || 0),
        completed: lesson.completed || false
      }));
    } catch (error) {
      return [];
    }
  },

  // ============================================================================
  // COURSES API
  // ============================================================================
  
  /**
   * Get enrolled courses with lessons
   * GET /api/student/courses
   * Purpose: Get all courses student is enrolled in with their lessons
   * @returns {Promise} Response with courses and lessons data
   *
   * ✅ PERFORMANCE FIXED: Converted from N+1 to 2 requests total
   * - First request: GET /student/courses (to get courses list)
   * - Second request: GET /student/lessons?courseIds=... (get all lessons at once)
   */
  getEnrolledCourses: async (params = {}) => {
    try {
      // First get the courses list
      const response = await api.get('/student/courses', { params });
      const courses = response.data.courses || [];

      if (courses.length === 0) {
        return {
          ...response.data,
          courses: []
        };
      }

      // Extract course IDs and fetch all lessons in single request
      const courseIds = courses.map(course => course.id).filter(Boolean);

      if (courseIds.length === 0) {
        return {
          ...response.data,
          courses: courses.map(course => ({ ...course, lessons: [] }))
        };
      }

      // Get all lessons for all courses in single request
      const lessonsQueryParams = new URLSearchParams({
        courseIds: courseIds.join(',')
      });

      const lessonsResponse = await api.get(`/student/lessons?${lessonsQueryParams}`);
      const allLessons = Array.isArray(lessonsResponse.data) ? lessonsResponse.data : (lessonsResponse.data.lessons || lessonsResponse.data || []);

      // Group lessons by course_id
      const lessonsByCourse = allLessons.reduce((acc, lesson) => {
        const courseId = lesson.course_id;
        if (!acc[courseId]) {
          acc[courseId] = [];
        }
        acc[courseId].push(lesson);
        return acc;
      }, {});

      // Attach lessons to courses
      const coursesWithLessons = courses.map(course => ({
        ...course,
        lessons: lessonsByCourse[course.id] || []
      }));

      return {
        ...response.data,
        courses: coursesWithLessons
      };
    } catch (error) {
      return {
        courses: [],
        total: 0,
        page: 1,
        totalPages: 0
      };
    }
  },

  /**
   * Get student statistics
   * GET /api/student/stats
   * Purpose: Get student's learning statistics
   * @returns {Promise} Response with student stats
   */
  getStudentStats: async () => {
    const response = await api.get('/student/stats');
    return response.data;
  },

  // ============================================================================
  // MESSAGES API
  // ============================================================================
  
  /**
   * Get student messages
   * GET /api/student/messages
   * Purpose: List all messages received by student
   * @returns {Promise} Response with messages array
   */
  getMessages: async () => {
    const response = await api.get('/student/messages');
    return response.data;
  },

  // ============================================================================
  // PUBLIC COURSES API
  // ============================================================================
  
  /**
   * Get public courses with filtering
   * GET /api/courses/public
   * Purpose: Browse available courses with search and filters
   * @param {Object} params - Search and filter parameters
   * @returns {Promise} Response with courses array
   */
  getCourses: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/courses/public?${queryParams}`);
    return response.data;
  },

  // ============================================================================
  // SUBSCRIPTION SYSTEM API
  // ============================================================================
  
  /**
   * Activate subscription code
   * POST /api/subscription-system/activate-code
   * Purpose: Activate a subscription code for student
   * @param {Object} data - Activation code and related info
   * @returns {Promise} Response with activation result
   */
  activateCode: async (data) => {
    const response = await api.post('/subscription-system/activate-code', data);
    return response.data;
  },

  /**
   * Submit payment request
   * POST /api/subscription-system/payment-request
   * Purpose: Submit payment request for subscription
   * @param {FormData} data - Payment request data with files
   * @returns {Promise} Response with payment request result
   */
  submitPaymentRequest: async (data) => {
    const response = await api.post('/subscription-system/payment-request', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Get current user's payment requests
   * GET /api/subscription-system/payment-requests/mine
   */
  getMyPaymentRequests: async () => {
    const response = await api.get('/subscription-system/payment-requests/mine');
    return response.data;
  },

  /**
   * Get available courses for subscription
   * GET /api/student/available-courses
   * Purpose: List courses available for student to subscribe
   * @returns {Promise} Response with available courses
   */
  getAvailableCourses: async () => {
    const response = await api.get('/courses/available');
    return response.data;
  },

  // ============================================================================
  // SCHEDULE API
  // ============================================================================
  
  /**
   * Get student schedule
   * GET /api/student/schedule
   * Purpose: Get student's class schedule
   * @returns {Promise} Response with schedule data
   */
  getStudentSchedule: async () => {
    const response = await api.get('/student/schedule');
    return response.data;
  },

  // ============================================================================
  // LESSONS API
  // ============================================================================
  
  /**
   * Get course details by ID
   * GET /api/courses/:courseId
   * Purpose: Get detailed information about a specific course
   * @param {number} courseId - ID of the course
   * @returns {Promise} Response with course details
   */
  getCourse: async (courseId) => {
    const response = await api.get(`/courses/${courseId}`);
    return response.data;
  },

  /**
   * Get course packages/pricing
   * GET /api/courses/:courseId/packages
   * Purpose: Get available packages and pricing for a course
   * @param {number} courseId - ID of course
   * @returns {Promise} Response with packages data
   */
  getCoursePackages: async (courseId) => {
    const response = await api.get(`/courses/${courseId}/packages`);
    return response.data;
  },

  /**
   * Get course lessons
   * GET /api/courses/:courseId/lessons
   * Purpose: List all lessons in a specific course
   * @param {number} courseId - ID of course
   * @returns {Promise} Response with lessons array
   */
  getCourseLessons: async (courseId) => {
    const response = await api.get(`/courses/${courseId}/lessons`);
    return response.data;
  },

  // ============================================================================
  // CERTIFICATES API
  // ============================================================================
  
  /**
   * Get student certificates
   * GET /api/student/certificates
   * Purpose: List all certificates earned by student
   * @returns {Promise} Response with certificates array
   */
  getCertificates: async () => {
    const response = await api.get('/student/certificates');
    return response.data;
  },

  // ============================================================================
  // PROFILE API
  // ============================================================================
  
  /**
   * Get student profile
   * GET /api/student/profile/:studentId
   * Purpose: Get student's profile information
   * @param {number} studentId - ID of the student
   * @returns {Promise} Response with student profile data
   */
  getStudentProfile: async (studentId) => {
    const response = await api.get(`/student/profile/${studentId}`);
    return response.data;
  },

  /**
   * Get course filters
   * GET /api/courses/filters
   * Purpose: Get available filter options for courses
   * @returns {Promise} Response with filter options
   */
  getCourseFilters: async () => {
    if (courseFiltersCache) return courseFiltersCache;
    const response = await api.get('/public/filters');
    courseFiltersCache = response.data.filters;
    return courseFiltersCache;
  },

  /**
   * Get subjects
   * GET /api/public/subjects
   * Purpose: List all available subjects
   * @returns {Promise} Response with subjects array
   */
  getSubjects: async () => {
    if (subjectsCache) return subjectsCache;
    const response = await api.get('/public/subjects');
    subjectsCache = response.data;
    return subjectsCache;
  },

  // ============================================================================
  // STUDENT SUBJECTS & TEACHERS API
  // ============================================================================
  
  /**
   * Get available subjects for student's grade
   * GET /api/student/subjects
   * Purpose: List subjects available for student based on grade
   * @returns {Promise} Response with subjects array
   */
  getStudentSubjects: async () => {
    const response = await api.get('/student/subjects');
    return response.data;
  },

  /**
   * Get all approved teachers for messaging
   * GET /api/student/all-teachers
   * Purpose: Get list of all teachers for composing new message
   * @param {Object} params - Query parameters (search)
   * @returns {Promise} Response with teachers array
   */
  getStudentTeachers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/student/all-teachers?${queryParams}`);
    return response.data;
  },

  /**
   * Get stages data for filtering
   * GET /api/public/stages
   * Purpose: Get all educational stages for filters
   * @returns {Promise} Response with stages array
   */
  getStages: async () => {
    if (stagesCache) return stagesCache;
    const response = await api.get('/public/stages');
    stagesCache = response.data;
    return stagesCache;
  },

  /**
   * Get categories/subjects data for filtering
   * GET /api/public/subjects
   * Purpose: Get all subjects for filters
   * @returns {Promise} Response with subjects array
   */
  getCategories: async () => {
    if (categoriesCache) return categoriesCache;
    const response = await api.get('/public/subjects');
    categoriesCache = response.data;
    return categoriesCache;
  },

  /**
   * Get student courses for filtering
   * GET /api/student/courses
   * Purpose: Get all student courses for filters
   * @returns {Promise} Response with courses array
   */
  getStudentCourses: async () => {
    const response = await api.get('/student/courses');
    return response.data;
  },

  // ============================================================================
  // STUDENT QUESTIONS API
  // ============================================================================
  
  /**
   * Get student's questions
   * GET /api/students/questions
   * Purpose: List all questions asked by student
   * @param {Object} params - Query parameters (answered, page, limit)
   * @returns {Promise} Response with questions array
   */
  getStudentQuestions: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key]) queryParams.append(key, params[key]);
    });
    const response = await api.get(`/student/questions?${queryParams}`);
    return response.data;
  },

  /**
   * Update a question
   * PUT /api/students/questions/:questionId
   * Purpose: Edit existing question
   * @param {number} questionId - ID of the question
   * @param {Object} questionData - Updated question data
   * @returns {Promise} Response with updated question
   */
  updateQuestion: async (questionId, questionData) => {
    const response = await api.put(`/student/questions/${questionId}`, questionData);
    return response.data;
  },

  /**
   * Delete a question
   * DELETE /api/students/questions/:questionId
   * Purpose: Remove question from system
   * @param {number} questionId - ID of the question
   * @returns {Promise} Response confirming deletion
   */
  deleteQuestion: async (questionId) => {
    const response = await api.delete(`/student/questions/${questionId}`);
    return response.data;
  },

  // ============================================================================
  // STUDENT PROGRESS API
  // ============================================================================
  
  /**
   * Get student progress overview
   * GET /api/students/progress
   * Purpose: Get overall learning progress
   * @returns {Promise} Response with progress data
   */
  getStudentProgress: async () => {
    const response = await api.get('/student/progress');
    return response.data;
  },

  /**
   * Get progress by subject
   * GET /api/students/progress/subjects
   * Purpose: Get progress breakdown by subject
   * @returns {Promise} Response with subject-wise progress
   */
  getProgressBySubjects: async () => {
    const response = await api.get('/student/progress/subjects');
    return response.data;
  },

  /**
   * Get student statistics by ID (for teachers and admins)
   * GET /api/students/stats/:studentId
   * Purpose: Get detailed statistics for specific student
   * @param {number} studentId - ID of the student
   * @returns {Promise} Response with student statistics
   */
  getStudentStatsById: async (studentId) => {
    const response = await api.get(`/student/stats/${studentId}`);
    return response.data;
  },

  // ============================================================================
  // LESSON DETAIL API
  // ============================================================================

  /**
   * Get lesson details by ID
   * GET /api/lessons/:lessonId
   * Purpose: Get detailed information about a specific lesson
   * @param {number} lessonId - ID of the lesson
   * @returns {Promise} Response with lesson details
   */
  getLesson: async (lessonId) => {
    const response = await api.get(`/lessons/${lessonId}`);
    return response.data;
  },

  /**
   * Get secure signed video URL for lesson playback
   * GET /api/lessons/:lessonId/play
   * Purpose: Get secure Token-authenticated URL for Bunny Stream videos
   * @param {number} lessonId - ID of the lesson
   * @returns {Promise} Response with signed video URL
   */
  getLessonPlayUrl: async (lessonId) => {
    const response = await api.get(`/lessons/${lessonId}/play`);
    return response.data;
  },

  /**
   * Get lesson comments
   * GET /api/lessons/:lessonId/comments
   * Purpose: Get all comments for a specific lesson
   * @param {number} lessonId - ID of the lesson
   * @returns {Promise} Response with comments data
   */
  getLessonComments: async (lessonId) => {
    const response = await api.get(`/lessons/${lessonId}/comments`);
    return response.data;
  },

  /**
   * Get lesson questions
   * GET /api/lessons/:lessonId/questions
   * Purpose: Get all questions for a specific lesson
   * @param {number} lessonId - ID of the lesson
   * @returns {Promise} Response with questions data
   */
  getLessonQuestions: async (lessonId) => {
    const response = await api.get(`/lessons/${lessonId}/questions`);
    return response.data;
  },

  
  /**
   * Update lesson progress (completed, lastWatchedPosition)
   * PUT /api/student/lessons/:lessonId/progress
   * Purpose: Update student's progress for a specific lesson (e.g. mark completed)
   * @param {number} lessonId - ID of the lesson
   * @param {Object} progressData - Progress data (completed, lastWatchedPosition)
   * @returns {Promise} Response with updated progress
   */
  updateLessonProgress: async (lessonId, progressData) => {
    const response = await api.put(`/student/lessons/${lessonId}/progress`, progressData);
    return response.data;
  },

  /**
   * Ask a question
   * POST /api/student/lessons/:lessonId/questions
   * Purpose: Ask a question about a lesson
   * @param {number} lessonId - ID of the lesson
   * @param {Object} questionData - Question data (text, isPublic)
   * @returns {Promise} Response with created question
   */
  askQuestion: async (lessonId, questionData) => {
    const response = await api.post(`/student/lessons/${lessonId}/questions`, questionData);
    return response.data;
  },

  /**
   * Get student questions
   * GET /api/student/questions
   * Purpose: Get all questions asked by the student
   * @param {Object} params - Query parameters (answered, lessonId, courseId, search, page, limit)
   * @returns {Promise} Response with questions data
   */
  getQuestions: async (params = {}) => {
    const response = await api.get('/student/questions', { params });
    return response.data;
  },

  /**
   * Get lesson analytics (video statistics from Bunny Stream)
   * GET /api/lessons/:lessonId/analytics
   * Purpose: Get detailed video analytics (views, watch time, etc.)
   * @param {number} lessonId - ID of the lesson
   * @returns {Promise} Response with analytics data
   */
  getLessonAnalytics: async (lessonId) => {
    try {
      const response = await api.get(`/lessons/${lessonId}/analytics`);
      return response.data;
    } catch (error) {
      return {
        success: false,
        data: {
          stats: {
            views: 0,
            totalWatchTime: 0,
            averageWatchTime: 0,
            duration: 0,
            completionRate: 0
          }
        }
      };
    }
  },

  /**
   * Rate a lesson
   * POST /api/lessons/:lessonId/rate
   * Purpose: Rate a lesson with a rating value and optional review
   * @param {number} lessonId - ID of lesson
   * @param {Object} ratingData - Rating data (rating, review)
   * @returns {Promise} Response with rating result
   */
  rateLesson: async (lessonId, ratingData) => {
    const response = await api.post(`/lessons/${lessonId}/rate`, ratingData);
    return response.data;
  },

  /**
   * Send a new message to a teacher
   * POST /api/student/messages
   * Purpose: Send a new message to a teacher/admin
   * @param {Object} messageData - Message data (recipient_id, subject, message, priority)
   * @returns {Promise} Response with created message
   */
  sendMessage: async (messageData) => {
    const response = await api.post('/student/messages', messageData);
    return response.data;
  },

  /**
   * Get message threads (conversations grouped by user)
   * GET /api/student/messages/threads
   * Purpose: Get all conversations as thread previews
   * @returns {Promise} Response with threads array
   */
  getMessageThreads: async () => {
    const response = await api.get('/student/messages/threads');
    return response.data;
  },

  /**
   * Get conversation thread with specific user
   * GET /api/student/messages/thread/:userId
   * Purpose: Get all messages in conversation with specific user
   * @param {number} userId - ID of the other user
   * @returns {Promise} Response with messages in conversation
   */
  getConversationThread: async (userId) => {
    const response = await api.get(`/student/messages/thread/${userId}`);
    return response.data;
  },

  /**
   * Delete a message
   * DELETE /api/student/messages/:messageId
   * Purpose: Delete a message from the inbox
   * @param {number} messageId - ID of the message
   * @returns {Promise} Response confirming deletion
   */
  deleteMessage: async (messageId) => {
    const response = await api.delete(`/student/messages/${messageId}`);
    return response.data;
  },

  /**
   * Mark a message as read
   * PUT /api/student/messages/:messageId/read
   * Purpose: Mark a message as read
   * @param {number} messageId - ID of the message
   * @returns {Promise} Response with updated message
   */
  markMessageAsRead: async (messageId) => {
    const response = await api.put(`/student/messages/${messageId}/read`);
    return response.data;
  },

  /**
   * Star a message
   * PUT /api/student/messages/:messageId/star
   * Purpose: Mark a message as starred/important
   * @param {number} messageId - ID of the message
   * @returns {Promise} Response with updated message
   */
  starMessage: async (messageId) => {
    const response = await api.put(`/student/messages/${messageId}/star`);
    return response.data;
  },

  /**
   * Unstar a message
   * DELETE /api/student/messages/:messageId/star
   * Purpose: Remove star from a message
   * @param {number} messageId - ID of the message
   * @returns {Promise} Response with updated message
   */
  unstarMessage: async (messageId) => {
    const response = await api.delete(`/student/messages/${messageId}/star`);
    return response.data;
  },

  /**
   * Get starred messages
   * GET /api/student/messages/starred
   * Purpose: Get all starred/important messages
   * @returns {Promise} Response with array of starred messages
   */
  getStarredMessages: async () => {
    const response = await api.get('/student/messages/starred');
    return response.data;
  },

  /**
   * Update typing status
   * PUT /api/student/typing-status
   * Purpose: Indicate that user is typing
   * @param {number} conversationUserId - ID of the other user in conversation
   * @param {boolean} isTyping - Whether user is currently typing
   * @returns {Promise} Response with status update
   */
  updateTypingStatus: async (conversationUserId, isTyping) => {
    const response = await api.put('/student/typing-status', {
      conversationUserId,
      isTyping
    });
    return response.data;
  },

  /**
   * Get typing status for a conversation
   * GET /api/student/typing-status/:conversationUserId
   * Purpose: Check which users are typing in a conversation
   * @param {number} conversationUserId - ID of the other user
   * @returns {Promise} Response with list of users typing
   */
  getTypingStatus: async (conversationUserId) => {
    const response = await api.get(`/student/typing-status/${conversationUserId}`);
    return response.data;
  },

  /**
   * Clear typing status
   * DELETE /api/student/typing-status
   * Purpose: Clear all typing statuses for user (on disconnect)
   * @returns {Promise} Response confirming clear
   */
  clearTypingStatus: async () => {
    const response = await api.delete('/student/typing-status');
    return response.data;
  },

  /**
   * Reply to a message
   * POST /api/student/messages/:messageId/reply
   * Purpose: Reply to a received message
   * @param {number} messageId - ID of the message to reply to
   * @param {Object} replyData - Reply message content
   * @returns {Promise} Response with created reply
   */
  replyToMessage: async (messageId, replyData) => {
    const response = await api.post(`/student/messages/${messageId}/reply`, replyData);
    return response.data;
  },

  /**
   * Delete entire conversation with a teacher
   * DELETE /api/student/conversations/:userId
   * Purpose: Delete all messages in conversation with specific teacher
   * @param {number} userId - ID of the teacher
   * @returns {Promise} Response with deletion result
   */
  deleteConversation: async (userId) => {
    const response = await api.delete(`/student/conversations/${userId}`);
    return response.data;
  },

  /**
   * Get teachers list for student
   * GET /api/student/all-teachers
   * Purpose: Get all teachers that student has access to
   * @returns {Promise} Response with teachers array
   */
  getTeachersList: async () => {
    const response = await api.get('/student/all-teachers');
    return response.data;
  },

  // ============================================================================
  // TEACHER RATING SYSTEM
  // ============================================================================

  /**
   * Rate a teacher
   * POST /api/student/teachers/:teacherId/rate
   * Purpose: Rate a teacher (once per student)
   * @param {number} teacherId - ID of teacher
   * @param {Object} ratingData - Rating data (rating, review)
   * @returns {Promise} Response with rating result
   */
  rateTeacher: async (teacherId, ratingData) => {
    const response = await api.post(`/student/teachers/${teacherId}/rate`, ratingData);
    return response.data;
  },

  /**
   * Get current student's rating for a teacher
   * GET /api/student/teachers/:teacherId/my-rating
   * Purpose: Get student's existing rating for a teacher
   * @param {number} teacherId - ID of teacher
   * @returns {Promise} Response with rating data
   */
  getMyTeacherRating: async (teacherId) => {
    const response = await api.get(`/student/teachers/${teacherId}/my-rating`);
    return response.data;
  },

  /**
   * Get teacher ratings and statistics
   * GET /api/student/teachers/:teacherId/ratings
   * Purpose: Get all ratings and statistics for a teacher
   * @param {number} teacherId - ID of teacher
   * @param {Object} params - Query parameters (page, limit)
   * @returns {Promise} Response with ratings and statistics
   */
  getTeacherRatings: async (teacherId, params = {}) => {
    const response = await api.get(`/student/teachers/${teacherId}/ratings`, { params });
    return response.data;
  }
};

export default studentService;
